import React, { useRef, useEffect, useState, useMemo, useContext, useCallback, Children } from 'react';
import warning from 'warning';
import { forwardRefWithAs, makeId, useDescendants, noop, checkStyles, DescendantProvider, boolOrBoolString, useForkedRef, useIsomorphicLayoutEffect, wrapEvent, cloneValidElement, useDescendant, useUpdateEffect, createDescendantContext, createNamedContext } from '@reach/utils';
import { useId } from '@reach/auto-id';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var TabsDescendantsContext = /*#__PURE__*/
createDescendantContext("TabsDescendantsContext");
var TabPanelDescendantsContext = /*#__PURE__*/
createDescendantContext("TabPanelDescendantsContext");
var TabsContext = /*#__PURE__*/
createNamedContext("TabsContext", {});
var useTabsContext = function useTabsContext() {
  return useContext(TabsContext);
}; ////////////////////////////////////////////////////////////////////////////////

/**
 * Tabs
 *
 * The parent component of the tab interface.
 *
 * @see Docs https://reacttraining.com/reach-ui/tabs#tabs
 */

var Tabs = /*#__PURE__*/
forwardRefWithAs(function Tabs(_ref, ref) {
  var _props$id;
  var _ref$as = _ref.as,
    Comp = _ref$as === void 0 ? "div" : _ref$as,
    children = _ref.children,
    defaultIndex = _ref.defaultIndex,
    _ref$index = _ref.index,
    controlledIndex = _ref$index === void 0 ? undefined : _ref$index,
    onChange = _ref.onChange,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    props = _objectWithoutPropertiesLoose(_ref, ["as", "children", "defaultIndex", "index", "onChange", "readOnly"]);
  var isControlled = useRef(controlledIndex != null);
  useEffect(function () {
    if (process.env.NODE_ENV !== "production") {
      process.env.NODE_ENV !== "production" ? warning(!(isControlled.current && controlledIndex == null), "Tabs is changing from controlled to uncontrolled. Tabs should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled Tabs for the lifetime of the component. Check the `index` prop being passed in.") : void 0;
      process.env.NODE_ENV !== "production" ? warning(!(!isControlled.current && controlledIndex != null), "Tabs is changing from uncontrolled to controlled. Tabs should not switch from uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled Tabs for the lifetime of the component. Check the `index` prop being passed in.") : void 0;
    }
  }, [controlledIndex]);
  var _id = useId(props.id);
  var id = (_props$id = props.id) !== null && _props$id !== void 0 ? _props$id : makeId("tabs", _id);
  /*
   * We only manage focus if the user caused the update vs. a new controlled
   * index coming in.
   */

  var userInteractedRef = useRef(false);
  var selectedPanelRef = useRef(null);
  var _useState = useState(defaultIndex || 0),
    selectedIndex = _useState[0],
    setSelectedIndex = _useState[1];
  var _useDescendants = useDescendants(),
    tabs = _useDescendants[0],
    setTabs = _useDescendants[1];
  var context = useMemo(function () {
    return {
      isControlled: isControlled.current,
      selectedIndex: isControlled.current ? controlledIndex : selectedIndex,
      id: id,
      userInteractedRef: userInteractedRef,
      selectedPanelRef: selectedPanelRef,
      setSelectedIndex: isControlled.current ? noop : setSelectedIndex,
      onFocusPanel: function onFocusPanel() {
        var _selectedPanelRef$cur;
        (_selectedPanelRef$cur = selectedPanelRef.current) === null || _selectedPanelRef$cur === void 0 ? void 0 : _selectedPanelRef$cur.focus();
      },
      onSelectTab: readOnly ? noop : function (index) {
        userInteractedRef.current = true;
        onChange && onChange(index);
        if (!isControlled.current) {
          setSelectedIndex(index);
        }
      }
    };
  }, [controlledIndex, id, onChange, readOnly, selectedIndex]);
  useEffect(function () {
    return checkStyles("tabs");
  }, []);
  return React.createElement(DescendantProvider, {
    context: TabsDescendantsContext,
    items: tabs,
    set: setTabs
  }, React.createElement(TabsContext.Provider, {
    value: context
  }, React.createElement(Comp, Object.assign({}, props, {
    ref: ref,
    "data-reach-tabs": "",
    id: props.id
  }), children)));
});
Tabs.displayName = "Tabs";
if (process.env.NODE_ENV !== "production") {} ////////////////////////////////////////////////////////////////////////////////

/**
 * TabList
 *
 * The parent component of the tabs.
 *
 * @see Docs https://reacttraining.com/reach-ui/tabs#tablist
 */

var TabList = /*#__PURE__*/
forwardRefWithAs(function TabList(_ref2, forwardedRef) {
  var children = _ref2.children,
    _ref2$as = _ref2.as,
    Comp = _ref2$as === void 0 ? "div" : _ref2$as,
    onKeyDown = _ref2.onKeyDown,
    props = _objectWithoutPropertiesLoose(_ref2, ["children", "as", "onKeyDown"]);
  var _useTabsContext = useTabsContext(),
    isControlled = _useTabsContext.isControlled,
    onSelectTab = _useTabsContext.onSelectTab,
    onFocusPanel = _useTabsContext.onFocusPanel,
    setSelectedIndex = _useTabsContext.setSelectedIndex,
    selectedIndex = _useTabsContext.selectedIndex;
  var _useContext = useContext(TabsDescendantsContext),
    descendants = _useContext.descendants;
  var focusableTabs = useMemo(function () {
    var nodes = [];
    for (var i = 0; i < descendants.length; i++) {
      var element = descendants[i].element;
      if (element && !boolOrBoolString(element.dataset.disabled)) {
        nodes.push(element);
      }
    }
    return nodes;
  }, [descendants]);
  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);
  var isRTL = useRef(false);
  var getFocusIndices = useCallback(function () {
    /*
     * To detect RTL mode, we'll look first for <html dir="rtl">, as it is the
     * generally preferred method for handling RTL writing modes and is more
     * performant than checking computed styles (event though the computed
     * styles should take the dir attribute into account).
     */
    isRTL.current = false;
    if (ownRef.current && (ownRef.current.ownerDocument && ownRef.current.ownerDocument.dir === "rtl" || getStyle(ownRef.current, "direction") === "rtl")) {
      isRTL.current = true;
    }
    /*
     * We could be clever and ~~functional~~ here but we really shouldn't need
     * to loop through these arrays more than once.
     *
     * TODOZ: We may want to check the document's active element here instead
     *       instead of the selectedIndex, even though you *shouldn't* be able
     *       to to focus a tab unless it's selected.
     */

    var selectedFocusIndex = focusableTabs.findIndex(function (element) {
      return element === descendants[selectedIndex].element;
    });
    var first = -1;
    var last = -1;
    var prev = -1;
    var next = -1;
    for (var n = 0; n < descendants.length; n++) {
      var element = descendants[n].element;
      if (element === focusableTabs[0]) {
        first = n;
      }
      if (element === focusableTabs[focusableTabs.length - 1]) {
        last = n;
      }
      if (element === focusableTabs[selectedFocusIndex - 1]) {
        prev = n;
      }
      if (element === focusableTabs[selectedFocusIndex + 1]) {
        next = n;
      }
    }
    return {
      first: first,
      last: last,
      prev: prev === -1 ? last : prev,
      next: next === -1 ? first : next
    };
  }, [descendants, focusableTabs, selectedIndex]); // TODOZ: Determine proper behavior for Home/End key in RTL mode.

  function handleKeyDown(event) {
    var key = event.key; // Bail if we aren't navigating

    if (!(key === "ArrowLeft" || key === "ArrowRight" || key === "ArrowDown" || key === "Home" || key === "End")) {
      return;
    }
    var _getFocusIndices = getFocusIndices(),
      first = _getFocusIndices.first,
      last = _getFocusIndices.last,
      prev = _getFocusIndices.prev,
      next = _getFocusIndices.next;
    switch (key) {
      case "ArrowRight":
        onSelectTab(isRTL.current ? prev : next);
        break;
      case "ArrowLeft":
        onSelectTab(isRTL.current ? next : prev);
        break;
      case "ArrowDown":
        // don't scroll down
        event.preventDefault();
        onFocusPanel();
        break;
      case "Home":
        onSelectTab(first);
        break;
      case "End":
        onSelectTab(last);
        break;
      default:
        return;
    }
  }
  useIsomorphicLayoutEffect(function () {
    var _descendants$selected, _descendants$selected2;

    /*
     * In the event an uncontrolled component's selected index is disabled,
     * (this should only happen if the first tab is disabled and no default
     * index is set), we need to override the selection to the next selectable
     * index value.
     */
    if (!isControlled && boolOrBoolString((_descendants$selected = descendants[selectedIndex]) === null || _descendants$selected === void 0 ? void 0 : (_descendants$selected2 = _descendants$selected.element) === null || _descendants$selected2 === void 0 ? void 0 : _descendants$selected2.dataset.disabled)) {
      var _getFocusIndices2 = getFocusIndices(),
        next = _getFocusIndices2.next;
      setSelectedIndex(next);
    }
  }, [descendants, getFocusIndices, isControlled, selectedIndex, setSelectedIndex]);
  return React.createElement(Comp, Object.assign({
    role: "tablist"
  }, props, {
    "data-reach-tab-list": "",
    ref: ref,
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown)
  }), Children.map(children, function (child, index) {
    /*
     * TODOZ: Since refactoring to use context rather than depending on
     * parent/child relationships, we need to update our recommendations for
     * animations that break when we don't forward the `isSelected` prop
     * to our tabs. We will remove this in 1.0 and update our docs
     * accordingly.
     */
    return cloneValidElement(child, {
      isSelected: index === selectedIndex
    });
  }));
});
TabList.displayName = "TabList";
if (process.env.NODE_ENV !== "production") {} ////////////////////////////////////////////////////////////////////////////////

/**
 * Tab
 *
 * The interactive element that changes the selected panel.
 *
 * @see Docs https://reacttraining.com/reach-ui/tabs#tab
 */

var Tab = /*#__PURE__*/
forwardRefWithAs(function Tab(_ref3, forwardedRef) {
  var children = _ref3.children,
    _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "button" : _ref3$as,
    disabled = _ref3.disabled,
    props = _objectWithoutPropertiesLoose(_ref3, ["children", "isSelected", "as", "disabled"]);
  var _useTabsContext2 = useTabsContext(),
    tabsId = _useTabsContext2.id,
    onSelectTab = _useTabsContext2.onSelectTab,
    selectedIndex = _useTabsContext2.selectedIndex,
    userInteractedRef = _useTabsContext2.userInteractedRef;
  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);
  var index = useDescendant({
    element: ownRef.current,
    context: TabsDescendantsContext
  });
  var isSelected = index === selectedIndex;
  function onSelect() {
    onSelectTab(index);
  }
  useUpdateEffect(function () {
    if (isSelected && ownRef.current && userInteractedRef.current) {
      userInteractedRef.current = false;
      ownRef.current.focus();
    }
  }, [isSelected]);
  return React.createElement(Comp, Object.assign({
    role: "tab"
  }, props, {
    ref: ref,
    "data-reach-tab": "",
    "data-disabled": disabled,
    "aria-controls": makeId(tabsId, "panel", index),
    "aria-disabled": disabled,
    "aria-selected": isSelected,
    "data-selected": isSelected ? "" : undefined,
    disabled: disabled,
    id: makeId(tabsId, "tab", index),
    onClick: onSelect,
    tabIndex: isSelected ? 0 : -1
  }), children);
});
Tab.displayName = "Tab";
if (process.env.NODE_ENV !== "production") {} ////////////////////////////////////////////////////////////////////////////////

/**
 * TabPanels
 *
 * The parent component of the panels.
 *
 * @see Docs https://reacttraining.com/reach-ui/tabs#tabpanels
 */

var TabPanels = /*#__PURE__*/
forwardRefWithAs(function TabPanels(_ref4, forwardedRef) {
  var children = _ref4.children,
    _ref4$as = _ref4.as,
    Comp = _ref4$as === void 0 ? "div" : _ref4$as,
    props = _objectWithoutPropertiesLoose(_ref4, ["children", "as"]);
  var _useDescendants2 = useDescendants(),
    tabPanels = _useDescendants2[0],
    setTabPanels = _useDescendants2[1];
  return React.createElement(DescendantProvider, {
    context: TabPanelDescendantsContext,
    items: tabPanels,
    set: setTabPanels
  }, React.createElement(Comp, Object.assign({}, props, {
    ref: forwardedRef,
    "data-reach-tab-panels": ""
  }), children));
});
TabPanels.displayName = "TabPanels";
if (process.env.NODE_ENV !== "production") {} ////////////////////////////////////////////////////////////////////////////////

/**
 * TabPanel
 *
 * The panel that displays when it's corresponding tab is active.
 *
 * @see Docs https://reacttraining.com/reach-ui/tabs#tabpanel
 */

var TabPanel = /*#__PURE__*/
forwardRefWithAs(function TabPanel(_ref5, forwardedRef) {
  var children = _ref5.children,
    _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    props = _objectWithoutPropertiesLoose(_ref5, ["children", "as"]);
  var _useTabsContext3 = useTabsContext(),
    selectedPanelRef = _useTabsContext3.selectedPanelRef,
    selectedIndex = _useTabsContext3.selectedIndex,
    tabsId = _useTabsContext3.id;
  var ownRef = useRef(null);
  var index = useDescendant({
    element: ownRef.current,
    context: TabPanelDescendantsContext
  });
  var isSelected = index === selectedIndex;
  var id = makeId(tabsId, "panel", index);
  var ref = useForkedRef(forwardedRef, ownRef, isSelected ? selectedPanelRef : null);
  return React.createElement(Comp, Object.assign({
    hidden: !isSelected,
    role: "tabpanel"
  }, props, {
    ref: ref,
    "data-reach-tab-panel": "",
    "aria-labelledby": makeId(tabsId, "tab", index),
    id: id,
    tabIndex: -1
  }), children);
});
TabPanel.displayName = "TabPanel";
if (process.env.NODE_ENV !== "production") {}
/**
 * Get a computed style value by property, backwards compatible with IE
 * @param element
 * @param styleProp
 */

function getStyle(element, styleProp) {
  var y = null;
  if (element.currentStyle) {
    y = element.currentStyle[styleProp];
  } else if (element.ownerDocument && element.ownerDocument.defaultView && typeof element.ownerDocument.defaultView.getComputedStyle === "function") {
    y = element.ownerDocument.defaultView.getComputedStyle(element, null).getPropertyValue(styleProp);
  }
  return y;
}
export { Tab, TabList, TabPanel, TabPanels, Tabs };